import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isMobile } from '@/biz/tool';
import WebModal from '@/components/web/components/Modal';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { useOrderListStore } from '@/hooks/useOrderListStore';
import ProductLogo from '@/components/web/components/ProductLogo';
import { Button } from 'aha-components';
import { normalizePriceUsd } from '@/utils/normalizePriceUsd';
import BottomDrawer from '@/components/web/components/BottomDrawer';
import { CoffeeIcon, RightIcon, BadgeIcon } from '@/components/common/icon';
import LottieClerkLoading from '@/components/web/components/LottieClerkLoading';
import { useTranslation } from 'react-i18next';
import VerificationContent from '@/components/web/components/ModalEmailVerification/verificationContent';

const ModalSelectInvitedOrderByNoMatch: React.FC = () => {
  const {
    setShowModalEmailVerification,
    showModalSelectInvitedOrderByNoMatch,
    setShowModalSelectInvitedOrderByNoMatch,
    showModalSelectInvitedOrderByNoMatchType: by
  } = useWebModalStore();
  const { orderListObject, isLoadingData } = useOrderListStore();
  const { active = [] } = orderListObject || {};
  const [noneOrderList, setNoneOrderList] = useState(false);

  useEffect(() => {
    if (!isLoadingData) {
      setNoneOrderList(!Boolean(active?.length));
    }
  }, [isLoadingData, active]);

  return (
    <>
      {isMobile() ? (
        <BottomDrawer
          isShowHeader={true}
          visible={showModalSelectInvitedOrderByNoMatch}
          isShowFooter={false}
          width={isMobile() ? 402 : 624}
          handleClose={() => {
            setShowModalSelectInvitedOrderByNoMatch(false);
          }}
          // 页面级别 最上层
          rootClassName="!z-[9999]"
          className={`${
            by === 'success'
              ? 'bg-gradient-to-b from-[#d9f1d9] via-[#f2faff] to-white'
              : 'bg-gradient-to-b from-[#ffdad0] via-[#fff6ee] to-white'
          }`}
          keepMounted={true}
        >
          <ModalContent noneOrderList={noneOrderList} />
        </BottomDrawer>
      ) : (
        <WebModal
          isShowHeader={true}
          visible={showModalSelectInvitedOrderByNoMatch}
          isShowFooter={false}
          width={isMobile() ? 402 : 810}
          handleClose={() => {
            setShowModalSelectInvitedOrderByNoMatch(false);
          }}
          className={`${
            by === 'success'
              ? 'bg-gradient-to-b from-[#d9f1d9] via-[#f2faff] to-white'
              : 'bg-gradient-to-b from-[#ffdad0] via-[#fff6ee] to-white'
          }`}
          keepMounted={true}
        >
          <ModalContent noneOrderList={noneOrderList} />
        </WebModal>
      )}
    </>
  );
};

export default ModalSelectInvitedOrderByNoMatch;

const ModalContent = ({ noneOrderList }: { noneOrderList: boolean }) => {
  const { t } = useTranslation();
  const {
    firstApplyVerifyEmailVerified,
    hasAppliedOrder,
    setShowModalEmailVerification,
    showModalSelectInvitedOrderByNoMatch,
    setShowModalSelectInvitedOrderByNoMatch,
    showModalSelectInvitedOrderByNoMatchType: by
  } = useWebModalStore();
  const { orderListObject, isLoadingData } = useOrderListStore();
  const { active = [] } = orderListObject || {};

  return (
    <>
      <div
        className={`w-full flex-1 flex-col justify-start items-center inline-flex  overflow-y-auto
          ${isMobile() ? 'h-[100vh]' : 'max-h-[70vh]'}
          `}
      >
        <div
          className={`flex flex-col px-4
        ${isMobile() ? 'gap-4' : ''}
        `}
        >
          {by === 'success' ? (
            <>
              <div
                className={`flex items-center justify-center text-center text-[#203c20] text-xl font-semibold font-['Geist']
                ${isMobile() ? 'flex-col' : 'flex-row'}
              `}
              >
                <>
                  <BadgeIcon className="w-[28px] h-[28px]" />
                  {t('modal.select_invited_order.success.title', {
                    defaultValue:
                      'Your interest has been successfully submitted.'
                  })}
                </>
              </div>
              <div className="self-stretch text-center justify-start mt-1">
                <span className="text-[#203c20]/60 text-base font-normal font-['Inter'] leading-normal">
                  {t('modal.select_invited_order.success.review_time', {
                    defaultValue:
                      'The brand may take 5 business days or longer to review your submission.'
                  })}
                  <br />
                  {t('modal.select_invited_order.success.avoid_extra_work', {
                    defaultValue: 'To avoid extra work,'
                  })}{' '}
                </span>
                <span className="text-[#203c20]/60 text-base font-bold font-['Inter'] leading-normal">
                  {t('modal.select_invited_order.success.wait_approval', {
                    defaultValue: 'please wait for approval'
                  })}
                </span>
                <span className="text-[#203c20]/60 text-base font-normal font-['Inter'] leading-normal">
                  {' '}
                  {t('modal.select_invited_order.success.before_creating', {
                    defaultValue: 'before creating your content.'
                  })}
                </span>
              </div>
            </>
          ) : (
            <>
              <div
                className={`flex items-center justify-center text-center text-[#5c281d] text-xl font-semibold font-['Geist']
                ${isMobile() ? 'flex-col' : 'flex-row'}
              `}
              >
                <>
                  <CoffeeIcon className="w-[28px] h-[28px]" />
                  {t('modal.select_invited_order.no_match.title', {
                    defaultValue:
                      "Your Audience Regions Do Not Match the Brand's Requirements"
                  })}
                </>
              </div>
              <div className="text-center text-[#5c281d]/60 text-base font-normal font-['Inter'] leading-normal">
                {t('modal.select_invited_order.no_match.description', {
                  defaultValue:
                    "We're sorry, but your audience regions do not align with the brand's current requirements. However, we have identified other opportunities that may be a better fit for you."
                })}
              </div>
            </>
          )}
        </div>
        {!firstApplyVerifyEmailVerified && !hasAppliedOrder ? (
          // 提醒新用户邮箱验证
          <div className="w-full mt-6">
            <VerificationContent
              imgWidth="w-[318px]"
              sendEmail={showModalSelectInvitedOrderByNoMatch}
            />
          </div>
        ) : (
          <div
            className={`w-full  grid gap-x-8 gap-y-3 mt-6 mb-10 bg-[#ffffff] rounded-l-3xl rounded-r-3xl px-6 pt-6
            ${isMobile() ? 'grid-cols-1 mx-[-10px]' : 'grid-cols-1'}`}
          >
            <div className="text-[#1c2024] text-base font-semibold font-['Geist'] leading-7">
              {t('modal.select_invited_order.more_invites', {
                defaultValue: 'More invites'
              })}
            </div>
            {/*  */}
            {isLoadingData ? (
              <div className="w-full h-full min-h-[200px] flex justify-center items-center">
                <LottieClerkLoading />
              </div>
            ) : !active?.length ? (
              <div className="flex flex-col justify-center items-center gap-1 my-14 text-[#8b8d98] text-sm font-normal font-['Inter'] leading-snug">
                <img src="/AiPC.png" alt="ai" className="w-[128px] h-[128px]" />
                {t('modal.select_invited_order.looking_for_orders', {
                  defaultValue:
                    'Aha Deepmatch is looking for suitable orders...'
                })}
              </div>
            ) : null}
            {/*  */}
            {active?.map((item: any, index: number) => (
              <div
                className={`w-full min-h-24 p-4 justify-between items-center inline-flex ${
                  index === active.length - 1 ? '' : 'border-b'
                } border-solid border-[#e8e8ec]
                ${isMobile() ? 'w-full' : 'w-[560px]'}`}
              >
                <div className="justify-start items-center gap-4 flex">
                  <ProductLogo src={item?.campaign?.product?.logo} size={64} />
                  <div className="flex-col justify-start items-start gap-1 inline-flex flex-1">
                    <div className="self-stretch justify-start items-center gap-1 inline-flex">
                      <div className="text-[#1c2024] text-base font-semibold font-['Plus Jakarta Sans'] leading-snug">
                        {normalizePriceUsd(item?.priceFields?.[0]?.price)}
                      </div>
                    </div>
                    <div className="self-stretch justify-start items-center gap-2 inline-flex">
                      <div className="justify-start items-start flex flex-wrap">
                        <div className="text-[#60646c] text-xs font-normal font-['Inter'] leading-tight">
                          {item?.campaign?.product?.name}
                        </div>
                        <div className="text-[#60646c] text-xs font-normal font-['Inter'] leading-tight">
                          ｜
                        </div>
                        <a
                          className="text-[#60646c] text-xs font-normal font-['Inter'] leading-tight break-all hover:text-[#0c67ef]"
                          href={item?.campaign?.product?.website}
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {item?.campaign?.product?.website}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                <Button
                  type="default"
                  className={`!w-[77px] !max-w-[77px] !h-[32px] !px-0 flex-shrink-0`}
                  onClick={() => {
                    // 关闭弹窗
                    setShowModalSelectInvitedOrderByNoMatch(false);
                    window.location.href = `/campaign/invites/detail/${item?.id}`;
                  }}
                >
                  {t('modal.select_invited_order.view_btn')}
                  <RightIcon />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      {/*  */}
      {(firstApplyVerifyEmailVerified || hasAppliedOrder) && (
        <>
          <div
            style={{
              display: !noneOrderList ? 'flex' : 'none'
            }}
            className={`w-full flex gap-3 border-t border-solid border-[#e8e8ec] pt-4 mt-4
        ${isMobile() ? 'flex-col' : 'justify-between'}`}
          >
            <div
              className={`${isMobile() ? 'order-2 flex justify-center' : 'justify-start'}`}
            >
              <span className="text-[#203C20] text-sm font-medium font-['Inter'] leading-5">
                {t('modal.select_invited_order.not_receiving_notifications', {
                  defaultValue: 'Not receiving notifications? '
                })}
              </span>
              <span
                className="text-[#FB6011] text-sm font-medium font-['Inter'] leading-5 cursor-pointer"
                onClick={() => {
                  setShowModalEmailVerification(true);
                }}
              >
                {t('modal.select_invited_order.verify_email', {
                  defaultValue: 'Verify your email'
                })}
              </span>
            </div>
            <div
              className={`flex gap-4 ${isMobile() ? 'order-1 justify-around' : ''}`}
            >
              <Button
                type="default"
                className="!w-[160px] !h-10"
                onClick={() => {
                  setShowModalSelectInvitedOrderByNoMatch(false);
                }}
              >
                {t('modal.select_invited_order.not_now', {
                  defaultValue: 'Not now'
                })}
              </Button>
              <Button
                type="primary"
                className="!w-[160px] !h-10"
                onClick={() => {
                  setShowModalSelectInvitedOrderByNoMatch(false);
                  window.location.href = `/campaign/invites`;
                }}
              >
                {t('modal.select_invited_order.view_all', {
                  defaultValue: 'View all'
                })}
              </Button>
            </div>
          </div>
        </>
      )}
      {/*  */}
      <div
        style={{
          display: noneOrderList ? 'flex' : 'none'
        }}
        className={`w-full flex gap-3 border-t border-solid border-[#e8e8ec] pt-4 mt-4
        ${isMobile() ? 'justify-center' : 'justify-end'}
        `}
      >
        <Button
          type="primary"
          className="!w-[160px] !h-10"
          onClick={() => {
            setShowModalSelectInvitedOrderByNoMatch(false);
          }}
        >
          {t('modal.select_invited_order.back_to_details', {
            defaultValue: 'Back to deal details'
          })}
        </Button>
      </div>
    </>
  );
};
